import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { createRole, deleteRole, updateRolePermissions } from '@/lib/api/admin'
import { ApiError } from '@/lib/api/client'

function getApiErrorCode(e: unknown): string | undefined {
  if (e instanceof ApiError) {
    const resp = e.response as { data?: { error?: { code?: string } } } | undefined
    return resp?.data?.error?.code
  }
  return undefined
}

export type AdminRole = {
  id: string
  label: string
  description: string
  permissions: string[]
}

export type PermissionDefinition = {
  id: string
  description: string
}

function resolvePermissionsDetailed(perms: string[], catalog: PermissionDefinition[]) {
  const byId = new Map(catalog.map((p) => [p.id, p]))
  return perms
    .map((id) => {
      const def = byId.get(id)
      return def
        ? { ...def, unknown: false }
        : {
            id,
            description: 'Permission inconnue (non référencée dans le catalogue).',
            unknown: true,
          }
    })
    .sort((a, b) => a.id.localeCompare(b.id))
}

interface RolesPermissionsTabProps {
  roles: AdminRole[]
  permissionsCatalog: PermissionDefinition[]
  isLoading: boolean
  onReload: () => Promise<void>
}

export function RolesPermissionsTab({
  roles,
  permissionsCatalog,
  isLoading,
  onReload,
}: RolesPermissionsTabProps) {
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([])
  const [isCreatingRole, setIsCreatingRole] = useState(false)

  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false)
  const [editRoleName, setEditRoleName] = useState('')
  const [editRolePermissions, setEditRolePermissions] = useState<string[]>([])
  const [isEditingRole, setIsEditingRole] = useState(false)

  const [roleSearch, setRoleSearch] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase()
    if (!q) return roles
    return roles.filter((r) => {
      return (
        r.label.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.permissions.some((p) => p.toLowerCase().includes(q))
      )
    })
  }, [roleSearch, roles])

  const selectedRole = useMemo(() => {
    return (
      filteredRoles.find((r) => r.id === selectedRoleId) ||
      filteredRoles[0] ||
      null
    )
  }, [filteredRoles, selectedRoleId])

  const selectedRoleGroups = useMemo(() => {
    return selectedRole ? resolvePermissionsDetailed(selectedRole.permissions, permissionsCatalog) : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole])

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h2 className="text-base font-semibold">Rôles & permissions</h2>
            <p className="text-xs text-muted-foreground">
              Définissez ce qu'un utilisateur peut faire, de manière business.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              placeholder="Rechercher un rôle ou une permission..."
              className="w-full sm:w-[380px]"
            />
            <Button
              variant="outline"
              onClick={() => {
                setNewRoleName('')
                setNewRolePermissions([])
                setCreateRoleDialogOpen(true)
              }}
              disabled={isLoading}
            >
              Nouveau rôle
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 border rounded-lg bg-background overflow-hidden">
            <div className="p-4">
              <div className="text-sm font-medium">Rôles</div>
              <div className="text-xs text-muted-foreground">
                Sélectionnez un rôle pour voir ses permissions.
              </div>
            </div>
            <Separator />
            <div className="p-2">
              {filteredRoles.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">Aucun résultat.</div>
              ) : (
                <div className="space-y-1">
                  {filteredRoles.map((role) => {
                    const isActive = role.id === (selectedRole?.id || '')
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setSelectedRoleId(role.id)}
                        className={
                          'w-full text-left rounded-md px-3 py-2 transition-colors ' +
                          (isActive ? 'bg-primary/10' : 'hover:bg-accent')
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{role.label}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">
                              {role.description}
                            </div>
                          </div>
                          <Badge variant="outline" className="shrink-0">
                            {role.permissions.length}
                          </Badge>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-3 border rounded-lg bg-background overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">Détail du rôle</div>
                  <div className="text-xs text-muted-foreground">
                    Permissions assignées au rôle sélectionné.
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedRole) {
                      setEditRoleName(selectedRole.id)
                      setEditRolePermissions([...selectedRole.permissions])
                      setEditRoleDialogOpen(true)
                    }
                  }}
                  disabled={!selectedRole}
                >
                  Modifier
                </Button>
              </div>

              {selectedRole ? (
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{selectedRole.label}</Badge>
                    <Badge variant="outline">{selectedRole.permissions.length} permissions</Badge>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {selectedRole.description}
                  </div>
                </div>
              ) : null}
            </div>

            <Separator />

            <div className="p-4 space-y-3">
              {selectedRole ? (
                <div className="space-y-2">
                  {selectedRoleGroups.map((p) => (
                    <div key={p.id} className="rounded-md border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm text-muted-foreground">{p.description}</div>
                          <div className="text-xs text-muted-foreground mt-1">{p.id}</div>
                        </div>
                        {('unknown' in p && p.unknown) ? (
                          <Badge variant="outline">Inconnue</Badge>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Sélectionnez un rôle.</div>
              )}
            </div>

          </div>
        </div>
      </div>

      <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouveau rôle</DialogTitle>
            <DialogDescription>
              Définissez un rôle et ses permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Nom du rôle</div>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Ex: Comptable, Chef de projet, etc."
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Permissions</div>
              <div className="text-xs text-muted-foreground mb-2">
                Sélectionnez les permissions pour ce rôle
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-md p-3">
                {permissionsCatalog.map((perm) => {
                  const isChecked = newRolePermissions.includes(perm.id)
                  return (
                    <label
                      key={perm.id}
                      className="flex items-start gap-3 p-2 rounded-md hover:bg-accent cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewRolePermissions([...newRolePermissions, perm.id])
                          } else {
                            setNewRolePermissions(newRolePermissions.filter((p) => p !== perm.id))
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{perm.id}</div>
                        <div className="text-xs text-muted-foreground">{perm.description}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateRoleDialogOpen(false)}
              disabled={isCreatingRole}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={async () => {
                const roleName = newRoleName.trim()
                if (!roleName) {
                  showErrorToast('Le nom du rôle est requis')
                  return
                }

                try {
                  setIsCreatingRole(true)
                  await createRole({ name: roleName, permissions: newRolePermissions })
                  showSuccessToast('Rôle créé')
                  setCreateRoleDialogOpen(false)
                  await onReload()
                } catch (e: unknown) {
                  console.error(e)
                  const errorCode = getApiErrorCode(e)
                  if (errorCode === 'ROLE_EXISTS') {
                    showErrorToast('Un rôle avec ce nom existe déjà')
                  } else {
                    showErrorToast('Erreur lors de la création du rôle')
                  }
                } finally {
                  setIsCreatingRole(false)
                }
              }}
              disabled={isCreatingRole}
            >
              {isCreatingRole ? 'Création...' : 'Créer le rôle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editRoleDialogOpen} onOpenChange={setEditRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
            <DialogDescription>
              Modifier les permissions ou supprimer le rôle.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Rôle</div>
              <div className="text-sm text-muted-foreground">
                {roles.find((r) => r.id === editRoleName)?.label || editRoleName}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Permissions</div>
              <div className="text-xs text-muted-foreground mb-2">
                Modifiez les permissions pour ce rôle
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-md p-3">
                {permissionsCatalog.map((perm) => {
                  const isChecked = editRolePermissions.includes(perm.id)
                  return (
                    <label
                      key={perm.id}
                      className="flex items-start gap-3 p-2 rounded-md hover:bg-accent cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditRolePermissions([...editRolePermissions, perm.id])
                          } else {
                            setEditRolePermissions(editRolePermissions.filter((p) => p !== perm.id))
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{perm.id}</div>
                        <div className="text-xs text-muted-foreground">{perm.description}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditRoleDialogOpen(false)}
              disabled={isEditingRole}
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={async () => {
                try {
                  setIsEditingRole(true)
                  await deleteRole(editRoleName)
                  showSuccessToast('Rôle supprimé')
                  setEditRoleDialogOpen(false)
                  await onReload()
                } catch (e: unknown) {
                  console.error(e)
                  const errorCode = getApiErrorCode(e)
                  if (errorCode === 'ROLE_HAS_USERS') {
                    showErrorToast('Impossible de supprimer un rôle assigné à des utilisateurs')
                  } else if (errorCode === 'ROLE_NOT_FOUND') {
                    showErrorToast('Rôle introuvable')
                  } else {
                    showErrorToast('Erreur lors de la suppression du rôle')
                  }
                } finally {
                  setIsEditingRole(false)
                }
              }}
              disabled={isEditingRole}
              className="w-full sm:w-auto"
            >
              {isEditingRole ? 'Suppression...' : 'Supprimer'}
            </Button>
            <Button
              type="button"
              onClick={async () => {
                try {
                  setIsEditingRole(true)
                  await updateRolePermissions(editRoleName, { permissions: editRolePermissions })
                  showSuccessToast('Permissions modifiées')
                  setEditRoleDialogOpen(false)
                  await onReload()
                } catch (e: unknown) {
                  console.error(e)
                  const errorCode = getApiErrorCode(e)
                  if (errorCode === 'LAST_ADMIN') {
                    showErrorToast('Impossible de retirer la permission users:manage du dernier administrateur')
                  } else if (errorCode === 'ROLE_NOT_FOUND') {
                    showErrorToast('Rôle introuvable')
                  } else {
                    showErrorToast('Erreur lors de la modification des permissions')
                  }
                } finally {
                  setIsEditingRole(false)
                }
              }}
              disabled={isEditingRole}
              className="w-full sm:w-auto"
            >
              {isEditingRole ? 'Modification...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
